import { useState, useRef, useEffect, useCallback } from "react";

/**
 * useCameraStream - Hook for managing camera stream
 * Handles getUserMedia, stream lifecycle, and video element setup
 */
export const useCameraStream = ({
	mode = "photo",
	facingMode = "user",
	delayInitialization = false,
	autoTriggerUpload = false,
	onError,
	onTrackEnded,
}) => {
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const [stream, setStream] = useState(null);
	const [hasPermission, setHasPermission] = useState(false);
	const [isInitializing, setIsInitializing] = useState(false);
	const [error, setError] = useState(null);

	// Stop camera stream
	const stopCamera = useCallback(() => {
		// Clear track check intervals
		if (window.cameraTrackCheckIntervals) {
			window.cameraTrackCheckIntervals.forEach((interval) =>
				clearInterval(interval)
			);
			window.cameraTrackCheckIntervals = [];
		}

		// Remove video pause listener
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}

		if (streamRef.current) {
			// Remove event listeners before stopping tracks
			streamRef.current.getTracks().forEach((track) => {
				track.stop();
			});
			streamRef.current = null;
			setStream(null);
		}
	}, []);

	// Start camera stream
	const startCamera = useCallback(async () => {
		// Don't start camera if we're in audio mode
		if (mode === "audio") {
			console.log("Skipping camera initialization for audio mode");
			return;
		}

		try {
			setError(null);
			setIsInitializing(true);

			// Stop existing stream if any
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
			}

			// Use minimal constraints for fastest initialization
			const wantsAudio = mode === "video";
			const constraints = {
				video: {
					facingMode,
					// Minimal constraints for fastest startup
					// Browser will use the best available resolution
				},
				audio: wantsAudio,
			};
			let mediaStream;
			try {
				mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
			} catch (err) {
				const audioRelated =
					wantsAudio &&
					(err?.name === "NotAllowedError" ||
						err?.name === "PermissionDeniedError" ||
						err?.name === "NotFoundError" ||
						err?.name === "DevicesNotFoundError" ||
						err?.name === "NotReadableError" ||
						err?.name === "TrackStartError" ||
						err?.name === "OverconstrainedError");
				if (!audioRelated) {
					throw err;
				}
				console.warn(
					"getUserMedia with audio failed, retrying video-only:",
					err?.name,
					err?.message
				);
				mediaStream = await navigator.mediaDevices.getUserMedia({
					video: constraints.video,
					audio: false,
				});
				if (onError) {
					onError(
						"Microphone access denied — recording will have no sound. Enable mic permission in your browser settings to record audio."
					);
				}
			}
			streamRef.current = mediaStream;
			setStream(mediaStream);
			setHasPermission(true);

			// Add event listeners to detect when camera/microphone is disconnected
			const handleTrackEnded = (event) => {
				const track = event.target;
				console.warn(
					"Media track ended unexpectedly:",
					track.kind,
					track.label
				);

				if (track.kind === "video") {
					const errorMsg =
						"Camera connection lost. Please check your camera connection and try again.";
					setError(errorMsg);
					setHasPermission(false);
					if (onError) {
						onError(errorMsg);
					}
					// Stop all tracks
					if (streamRef.current) {
						streamRef.current.getTracks().forEach((t) => t.stop());
						streamRef.current = null;
						setStream(null);
					}
				} else if (track.kind === "audio") {
					const errorMsg =
						"Microphone connection lost. Please check your microphone connection and try again.";
					setError(errorMsg);
					setHasPermission(false);
					if (onError) {
						onError(errorMsg);
					}
					if (onTrackEnded) {
						onTrackEnded(track);
					}
				}
			};

			// Add ended listeners to all tracks
			mediaStream.getTracks().forEach((track) => {
				track.addEventListener("ended", handleTrackEnded);
			});

			// Periodic check for track state (in case 'ended' event doesn't fire reliably)
			const trackCheckInterval = setInterval(() => {
				if (!streamRef.current) {
					clearInterval(trackCheckInterval);
					return;
				}

				const tracks = streamRef.current.getTracks();
				const videoTracks = tracks.filter((t) => t.kind === "video");
				const audioTracks = tracks.filter((t) => t.kind === "audio");

				// Check video tracks
				if (videoTracks.length > 0) {
					const activeVideoTracks = videoTracks.filter(
						(t) => t.readyState === "live"
					);
					if (activeVideoTracks.length === 0) {
						console.warn("Video tracks became inactive - camera disconnected");
						clearInterval(trackCheckInterval);
						handleTrackEnded({ target: videoTracks[0] });
					}
				}

				// Check audio tracks (only if in video mode)
				if (mode === "video" && audioTracks.length > 0) {
					const activeAudioTracks = audioTracks.filter(
						(t) => t.readyState === "live"
					);
					if (activeAudioTracks.length === 0 && onTrackEnded) {
						console.warn("Audio tracks became inactive during recording");
						clearInterval(trackCheckInterval);
						handleTrackEnded({ target: audioTracks[0] });
					}
				}
			}, 2000); // Check every 2 seconds

			// Store interval ID for cleanup
			if (!window.cameraTrackCheckIntervals) {
				window.cameraTrackCheckIntervals = [];
			}
			window.cameraTrackCheckIntervals.push(trackCheckInterval);

			if (videoRef.current) {
				const video = videoRef.current;
				console.log(
					"Setting stream on video element, stream tracks:",
					mediaStream.getVideoTracks().length
				);
				video.srcObject = mediaStream;
				// Apply transform for front-facing camera
				video.style.transform = facingMode === "user" ? "scaleX(-1)" : "none";

				// Ensure video is set up for autoplay
				video.muted = true;
				video.playsInline = true;
				video.autoplay = true;

				// Wait for video to be ready, then play
				const playVideo = async () => {
					try {
						console.log(
							"Attempting to play video, readyState:",
							video.readyState,
							"paused:",
							video.paused
						);
						// Wait a bit for the stream to be ready
						if (video.readyState >= 2) {
							await video.play();
							console.log("✅ Camera video started playing");
						} else {
							// Wait for loadedmetadata event
							const onLoadedMetadata = async () => {
								try {
									console.log("Metadata loaded, readyState:", video.readyState);
									await video.play();
									console.log(
										"✅ Camera video started playing (after metadata)"
									);
								} catch (err) {
									console.warn("❌ Error playing video after metadata:", err);
								}
								video.removeEventListener("loadedmetadata", onLoadedMetadata);
							};
							video.addEventListener("loadedmetadata", onLoadedMetadata);

							// Also try after a delay
							setTimeout(async () => {
								if (video.readyState >= 2 && video.paused) {
									try {
										await video.play();
										console.log("✅ Camera video started playing (delayed)");
									} catch (err) {
										console.warn("❌ Error playing video (delayed):", err);
									}
								}
							}, 300);
						}
					} catch (playError) {
						console.warn("❌ Video play error:", playError);
						// Try again after a short delay
						setTimeout(async () => {
							try {
								if (video.paused) {
									await video.play();
									console.log("✅ Camera video started playing (retry)");
								}
							} catch (retryError) {
								console.warn("❌ Video play retry error:", retryError);
							}
						}, 200);
					}
				};

				playVideo();
			} else {
				console.warn("⚠️ videoRef.current is null, cannot set stream");
			}

			// Mark as initialized immediately after stream is set
			setIsInitializing(false);
		} catch (err) {
			console.error("Error accessing camera:", err);
			const errorName = err?.name || err?.constructor?.name || "UnknownError";
			let errorMessage = "";

			if (
				errorName === "NotAllowedError" ||
				errorName === "PermissionDeniedError"
			) {
				errorMessage =
					"Camera access was denied. Please allow camera permissions in your browser settings and try again.";
			} else if (
				errorName === "NotFoundError" ||
				errorName === "DevicesNotFoundError"
			) {
				errorMessage =
					"No camera found. Please connect a camera and try again.";
			} else if (
				errorName === "NotReadableError" ||
				errorName === "TrackStartError"
			) {
				errorMessage =
					"Camera is already in use by another application. Please close other apps using the camera and try again.";
			} else {
				errorMessage =
					"Unable to access camera. Please check your browser permissions and try again.";
			}

			setError(errorMessage);
			setHasPermission(false);
			setIsInitializing(false);
			if (onError) {
				onError(errorMessage);
			}
		}
	}, [facingMode, mode, onError, onTrackEnded]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			stopCamera();
		};
	}, [stopCamera]);

	return {
		videoRef,
		streamRef,
		stream,
		hasPermission,
		isInitializing,
		error,
		setError,
		setStream,
		setHasPermission,
		setIsInitializing,
		startCamera,
		stopCamera,
	};
};

export default useCameraStream;
